import React, { useLayoutEffect, useRef, useState } from 'react';
import checkboxEmpty from '../images/ui-icons/checkbox-empty.svg';
import checkboxFilled from '../images/ui-icons/checkbox-filled.svg';
import { arrangeSections } from '../pages/reportSheetUtils';
import { patientIdentifiers, patientMrn } from '../utils/format';
import ReportIcon from './ReportIcon';
import UiIcon from './UiIcon';
import './PhoneReportScreen.css';

const MAX_TODOS_ON_CARD = 3; // as in the app (kabilah-react-native logic/report.ts)

// The app's card: icon, title and Edit pill, a divider, then the body with a
// chevron when there's more. Clicking a card shows the rest (the app opens it
// on its own screen).
function ReportCard({ icon, title, pinned, hasMore, expanded, onToggle, children }) {
  return (
    <section className={`app-card${pinned ? ' app-card-pinned' : ''}`}>
      <button type="button" className="app-card-hit" onClick={onToggle} aria-expanded={hasMore ? expanded : undefined}>
        <span className="app-card-title-row">
          <ReportIcon name={icon} />
          <span className="app-card-title">{title}</span>
          <span className="app-edit-pill" aria-hidden="true">Edit</span>
        </span>
        <span className="app-card-divider" />
        <span className="app-card-body-row">
          <span className="app-card-body">{children}</span>
          {hasMore && !expanded && <span className="app-chevron"><UiIcon name="chevron-right" size={20} /></span>}
        </span>
      </button>
    </section>
  );
}

// Section contents clamped to two lines like the app; reports whether there's more.
function Contents({ text, expanded, onOverflow }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) onOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded, onOverflow]);
  return <span ref={ref} className={`app-contents${expanded ? '' : ' app-contents-clamped'}`}>{text}</span>;
}

function SectionCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const empty = String(item.contents || '').trim() === '';
  return (
    <ReportCard icon={item.icon} title={item.name} pinned={item.pinned}
      hasMore={overflows} expanded={expanded} onToggle={() => setExpanded((v) => !v)}>
      {empty ? null : <Contents text={item.contents} expanded={expanded} onOverflow={setOverflows} />}
      {item.pinned && <span className="visually-hidden">(pinned)</span>}
    </ReportCard>
  );
}

function ToDoCard({ items }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, MAX_TODOS_ON_CARD);
  const title = items.length === 0 ? 'ToDo List' : `ToDo List (${Math.min(items.length, MAX_TODOS_ON_CARD)}/${items.length})`;
  return (
    <ReportCard icon="todo" title={title} hasMore={items.length > MAX_TODOS_ON_CARD}
      expanded={expanded} onToggle={() => setExpanded((v) => !v)}>
      {items.length > 0 && (
        <span className="app-todos">
          {shown.map((item, index) => (
            <span key={`${item.name}-${index}`} className={`app-todo${item.isChecked ? ' app-todo-done' : ''}`}>
              <img src={item.isChecked ? checkboxFilled : checkboxEmpty} alt="" width="24" height="24" />
              <span className="app-todo-text">{item.name}</span>
              <span className="visually-hidden">{item.isChecked ? '(done)' : '(not done)'}</span>
            </span>
          ))}
        </span>
      )}
    </ReportCard>
  );
}

// The round ‹ back button at the top left of the app's screens.
export const PhoneBackButton = ({ onBack, label = 'Back' }) => (
  <button type="button" className="app-circle-button" onClick={onBack} aria-label={label}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 5 8 12 15 19" />
    </svg>
  </button>
);

// The Kabilah app's report screen, for PhoneFrame: nav (‹ and edit), the
// patient header, the to-do list and each section as white cards on grey,
// pinned first, then the Handoff / Update / trash footer. Read-only: the
// app's controls are shown for the look only. ‹ calls onBack.
const PhoneReportScreen = ({ report, onBack, backLabel }) => {
  const { patient = {}, toDoList = [] } = report;
  const mrn = patientMrn(patient);
  const { sections, empty } = arrangeSections(report.reportItems);
  const readOnly = { 'aria-hidden': true, title: 'Changes are made in the Aren app' };
  return (
    <>
      {/* As in the app, the nav and patient header stay put; only the grey list scrolls. */}
      <div className="app-nav">
        <PhoneBackButton onBack={onBack} label={backLabel} />
        <span className="app-circle-button app-circle-muted" {...readOnly}><UiIcon name="edit" /></span>
      </div>
      <header className="app-header">
        <h1 className="app-name">{patient.name || 'Patient Name'}</h1>
        <p className="app-description">{patientIdentifiers(patient) || 'Gender, Age, Room Number, Admitted Date'}</p>
        {mrn && <p className="app-mrn">{mrn}</p>}
        <div className="app-pills" {...readOnly}>
          <span className="app-pill">EHR Export</span>
          <span className="app-pill">Transcript</span>
          <span className="app-pill">Transfer</span>
        </div>
      </header>
      <div className="app-scroll app-scroll-grey">
        <div className="app-list">
          <ToDoCard items={toDoList} />
          {[...sections, ...empty].map((item, index) => (
            <SectionCard key={`${item.name}-${index}`} item={item} />
          ))}
        </div>
      </div>
      <div className="app-footer" {...readOnly}>
        <span className="app-round app-round-red"><UiIcon name="microphone" size={20} />Handoff</span>
        <span className="app-round app-round-grey"><UiIcon name="microphone" size={20} />Update</span>
        <span className="app-round app-round-grey app-round-square"><UiIcon name="trash" /></span>
      </div>
    </>
  );
};

export default PhoneReportScreen;
